import React, { useMemo, useState } from 'react';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { School } from 'models/school';
import { resolveSchoolPublicSite } from 'helpers/publicSite';
import { KIND_SERVER_TO_CLIENT_MAPPING } from 'consts/school';
import './Subscribe.scss';

interface Props {
   school: School;
}

function getCalendarUrls(school: School) {
   const feedUrl = new URL(`${window.apiBase}/public/schools/${school.id}/calendar.ics`, window.location.href).toString();
   const webcalUrl = feedUrl.replace(/^https?:/i, 'webcal:');
   const isUnion = school.kind === KIND_SERVER_TO_CLIENT_MAPPING.SCHOOL_UNION;
   const name = `${school.name} ${isUnion ? 'tournaments' : 'sport'}`;

   return {
      feedUrl,
      webcalUrl,
      googleUrl: `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(webcalUrl)}`,
      outlookUrl: `https://outlook.live.com/calendar/0/addcalendar?url=${encodeURIComponent(feedUrl)}&name=${encodeURIComponent(name)}`,
   };
}

export default function Subscribe({ school }: Props) {
   const [copyLabel, setCopyLabel] = useState('Copy calendar link');
   const settings = resolveSchoolPublicSite(school);
   const isUnion = school.kind === KIND_SERVER_TO_CLIENT_MAPPING.SCHOOL_UNION;
   const urls = useMemo(() => getCalendarUrls(school), [school]);
   const isAvailable = settings.status === 'PUBLIC_AVAILABLE';

   const copyFeedUrl = async () => {
      try {
         await navigator.clipboard.writeText(urls.feedUrl);
         setCopyLabel('Link copied');
      } catch (_) {
         window.prompt('Copy this calendar link:', urls.feedUrl);
      }
      window.setTimeout(() => setCopyLabel('Copy calendar link'), 2500);
   };

   return (
      <main className='school-subscribe'>
         <header className='school-subscribe__heading'>
            <span className='school-subscribe__icon'><CalendarMonthRoundedIcon /></span>
            <div>
               <span>{isUnion ? 'Tournament calendar' : 'School sport calendar'}</span>
               <h1>{isUnion ? 'Never miss a tournament' : 'Never miss a fixture'}</h1>
               <p>Subscribe once and published {isUnion ? 'tournaments' : 'fixtures'} from {school.name} will appear in your calendar automatically.</p>
            </div>
         </header>

         {!isAvailable ? (
            <section className='school-subscribe__unavailable'>
               <h2>Calendar subscription is not available</h2>
               <p>This {isUnion ? 'union' : 'school'} website is protected. Calendar feeds are available only for fully public websites.</p>
            </section>
         ) : (
            <>
               <section className='school-subscribe__options' aria-label='Calendar subscription options'>
                  <a href={urls.webcalUrl}>
                     <strong>Apple Calendar</strong>
                     <span>Open the calendar app and subscribe</span>
                     <OpenInNewRoundedIcon aria-hidden='true' />
                  </a>
                  <a href={urls.googleUrl} target='_blank' rel='noreferrer'>
                     <strong>Google Calendar</strong>
                     <span>Add the school feed to Google Calendar</span>
                     <OpenInNewRoundedIcon aria-hidden='true' />
                  </a>
                  <a href={urls.outlookUrl} target='_blank' rel='noreferrer'>
                     <strong>Outlook</strong>
                     <span>Subscribe in Outlook on the web</span>
                     <OpenInNewRoundedIcon aria-hidden='true' />
                  </a>
               </section>

               <section className='school-subscribe__manual'>
                  <div>
                     <h2>Other calendar apps</h2>
                     <p>Copy the subscription link and choose “Add calendar by URL” in your calendar app.</p>
                  </div>
                  <code>{urls.feedUrl}</code>
                  <div className='school-subscribe__actions'>
                     <button type='button' onClick={copyFeedUrl}><ContentCopyRoundedIcon />{copyLabel}</button>
                     <a href={urls.feedUrl}><DownloadRoundedIcon />Download calendar file</a>
                  </div>
               </section>

               <aside className='school-subscribe__note'>
                  Calendar apps refresh subscriptions on their own schedule, so a newly published or changed {isUnion ? 'tournament' : 'fixture'} may take a few hours to appear.{!isUnion && ' Club events are not included.'}
               </aside>
            </>
         )}
      </main>
   );
}
